import { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '../components/ui/command';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import { format } from 'date-fns';
import axios from 'axios';
import { Plus, Search, Target, Edit, Trash2, DollarSign, CalendarIcon, Check, ChevronsUpDown, Users, Download, Clock, ChevronRight, Building2, User as UserIcon, Paperclip } from 'lucide-react';
import * as XLSX from 'xlsx';
import OpportunityDetailDialog from '../components/OpportunityDetailDialog';

const getDaysInStage = (opp) => {
  const stageDate = opp.stage_entered_at || opp.created_at;
  if (!stageDate) return 0;
  const entered = new Date(stageDate);
  const now = new Date();
  return Math.floor((now - entered) / (1000 * 60 * 60 * 24));
};

const getDaysColor = (days) => {
  if (days <= 7) return 'bg-[#ECFDF5] text-[#059669]';
  if (days <= 21) return 'bg-[#FFF7ED] text-[#D97706]';
  return 'bg-[#FEF2F2] text-[#DC2626]';
};

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SPORTS = [
  "Football", "Men's Basketball", "Women's Basketball", "Baseball", "Softball",
  "Men's Soccer", "Women's Soccer", "Men's Volleyball", "Women's Volleyball",
  "Men's Track & Field", "Women's Track & Field", "Men's Cross Country", "Women's Cross Country",
  "Men's Swimming & Diving", "Women's Swimming & Diving", "Men's Tennis", "Women's Tennis",
  "Men's Golf", "Women's Golf", "Men's Lacrosse", "Women's Lacrosse",
  "Men's Ice Hockey", "Women's Ice Hockey", "Field Hockey", "Wrestling",
  "Men's Gymnastics", "Women's Gymnastics", "Rowing", "Men's Water Polo", "Women's Water Polo",
  "Beach Volleyball", "Bowling", "Fencing", "Rifle", "Skiing", "Equestrian", "Triathlon", "Acrobatics & Tumbling"
];
const POLICY_TYPES = ["Critical Injury", "Performance Bonus", "Transfer Insurance", "Consulting"];
const POLICY_COLORS = {
  'Critical Injury': 'bg-[#CCFBF1] text-[#0D9488]',
  'Performance Bonus': 'bg-[#FEF3C7] text-[#D97706]',
  'Transfer Insurance': 'bg-[#EEF0FF] text-[#6B5CE7]',
  'Consulting': 'bg-[#F1F5F9] text-[#475569]',
};
const STAGES = [
  "Initial Conversation", "Qualified Opportunity", "Discovery", "Quote", "Negotiation", "Closed Won", "Closed Lost"
];
const STATUSES = ["Open", "Closed Won", "Closed Lost"];

const formatCurrency = (value) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0 }).format(value || 0);
const getInitials = (name) => name?.split(' ').map(n => n[0]).join('').toUpperCase() || 'U';
const getStatusColor = (status) => {
  switch (status) {
    case 'Open': return 'bg-[#007AFF]/10 text-[#007AFF]';
    case 'Closed Won': return 'bg-[#10B981]/10 text-[#059669]';
    case 'Closed Lost': return 'bg-[#FF3B30]/10 text-[#DC2626]';
    default: return 'bg-[#94A3B8]/10 text-[#64748B]';
  }
};

const OpportunitiesPage = () => {
  const { getAuthHeaders, user } = useAuth();
  const [opportunities, setOpportunities] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterOwner, setFilterOwner] = useState('all');
  const [filterSport, setFilterSport] = useState('all');
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [editOpp, setEditOpp] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
  const [expandedUnis, setExpandedUnis] = useState(new Set());
  const [detailOpp, setDetailOpp] = useState(null);

  const [formData, setFormData] = useState({
    account_id: '', sport: '', athlete_name: '', policy_type: '', estimated_premium: 0,
    owner_id: '', support_ids: [], stage: 'Initial Conversation', expected_close_date: '',
    status: 'Open', notes: '', department: '', timing: '', authority: '', budget: '', contact: ''
  });

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const headers = getAuthHeaders();
      const [oppsRes, accountsRes, usersRes] = await Promise.all([
        axios.get(`${API}/opportunities`, { headers }),
        axios.get(`${API}/accounts`, { headers }),
        axios.get(`${API}/users`, { headers })
      ]);
      setOpportunities(oppsRes.data);
      setAccounts(accountsRes.data);
      setUsers(usersRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load opportunities');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.account_id || !formData.sport || !formData.policy_type) {
      toast.error('University, Sport and Policy Type are required');
      return;
    }
    try {
      const payload = {
        account_id: formData.account_id,
        sport: formData.sport,
        athlete_name: formData.athlete_name || 'TBD',
        policy_type: formData.policy_type,
        estimated_premium: parseFloat(formData.estimated_premium) || 0,
        owner_id: formData.owner_id,
        support_ids: formData.support_ids,
        stage: formData.stage,
        status: formData.status,
        notes: formData.notes,
        department: formData.department,
        timing: formData.timing,
        authority: formData.authority,
        budget: formData.budget,
        contact: formData.contact,
        expected_close_date: selectedDate ? format(selectedDate, 'yyyy-MM-dd') : null
      };
      if (editOpp) {
        await axios.put(`${API}/opportunities/${editOpp.id}`, payload, { headers: getAuthHeaders() });
        toast.success('Policy updated successfully');
      } else {
        await axios.post(`${API}/opportunities`, payload, { headers: getAuthHeaders() });
        toast.success('Policy created successfully');
      }
      fetchData();
      resetForm();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to save policy';
      toast.error(typeof errorMsg === 'string' ? errorMsg : 'Failed to save policy');
    }
  };

  const handleDelete = async (oppId) => {
    if (!window.confirm('Are you sure you want to delete this policy?')) return;
    try {
      await axios.delete(`${API}/opportunities/${oppId}`, { headers: getAuthHeaders() });
      toast.success('Policy deleted successfully');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete policy');
    }
  };

  const resetForm = () => {
    setFormData({
      account_id: '', sport: '', athlete_name: '', policy_type: '', estimated_premium: 0,
      owner_id: user?.id || '', support_ids: [], stage: 'Initial Conversation', expected_close_date: '',
      status: 'Open', notes: '', department: '', timing: '', authority: '', budget: '', contact: ''
    });
    setSelectedDate(null);
    setIsDatePickerOpen(false);
    setEditOpp(null);
    setIsCreateOpen(false);
  };

  const openEditDialog = (opp) => {
    setEditOpp(opp);
    setFormData({
      account_id: opp.account_id,
      sport: opp.sport,
      athlete_name: opp.athlete_name === 'TBD' ? '' : (opp.athlete_name || ''),
      policy_type: opp.policy_type || opp.products?.[0]?.product_type || '',
      estimated_premium: opp.estimated_premium || opp.total_estimated_premium || 0,
      owner_id: opp.owner_id,
      support_ids: opp.support_ids || [],
      stage: opp.stage,
      expected_close_date: opp.expected_close_date,
      status: opp.status,
      notes: opp.notes || '',
      department: opp.department || '',
      timing: opp.timing || '',
      authority: opp.authority || '',
      budget: opp.budget || '',
      contact: opp.contact || ''
    });
    setSelectedDate(opp.expected_close_date ? new Date(opp.expected_close_date) : null);
    setIsCreateOpen(true);
  };

  const filteredOpportunities = opportunities.filter(opp => {
    const term = searchTerm.toLowerCase();
    const matchesSearch = opp.account?.university_name?.toLowerCase().includes(term) ||
      opp.sport?.toLowerCase().includes(term) ||
      opp.athlete_name?.toLowerCase().includes(term) ||
      opp.policy_type?.toLowerCase().includes(term);
    const matchesStatus = filterStatus === 'all' || opp.status === filterStatus;
    const matchesOwner = filterOwner === 'all' || opp.owner_id === filterOwner;
    const matchesSport = filterSport === 'all' || opp.sport === filterSport;
    return matchesSearch && matchesStatus && matchesOwner && matchesSport;
  });

  // Build University -> Sport -> Athlete -> Policies hierarchy with roll-up totals
  const grouped = useMemo(() => {
    const universities = {};
    filteredOpportunities.forEach(opp => {
      const uniId = opp.account_id || 'unknown';
      if (!universities[uniId]) {
        universities[uniId] = { id: uniId, name: opp.account?.university_name || 'Unknown University', account: opp.account, total: 0, count: 0, sports: {} };
      }
      const u = universities[uniId];
      u.total += opp.total_estimated_premium || 0; u.count += 1;
      const sportKey = opp.sport || 'Unspecified';
      if (!u.sports[sportKey]) u.sports[sportKey] = { name: sportKey, total: 0, athletes: {} };
      const s = u.sports[sportKey];
      s.total += opp.total_estimated_premium || 0;
      const athKey = opp.athlete_name || 'TBD';
      if (!s.athletes[athKey]) s.athletes[athKey] = { name: athKey, total: 0, policies: [] };
      const a = s.athletes[athKey];
      a.total += opp.total_estimated_premium || 0;
      a.policies.push(opp);
    });
    return Object.values(universities)
      .map(u => ({ ...u, sports: Object.values(u.sports).map(s => ({ ...s, athletes: Object.values(s.athletes).sort((x, y) => y.total - x.total) })).sort((x, y) => y.total - x.total) }))
      .sort((x, y) => y.total - x.total);
  }, [filteredOpportunities]);

  const toggleUni = (id) => {
    setExpandedUnis(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const exportToExcel = () => {
    const data = filteredOpportunities.map(opp => ({
      'University': opp.account?.university_name || '',
      'Conference': opp.account?.conference || '',
      'Division': opp.account?.division || '',
      'Sport': opp.sport || '',
      'Athlete': opp.athlete_name || '',
      'Policy Type': opp.policy_type || (opp.products || []).map(p => p.product_type).join(', '),
      'Stage': opp.stage || '',
      'Days in Stage': getDaysInStage(opp),
      'Status': opp.status || '',
      'Owner': opp.owner?.name || '',
      'Department': opp.department || '',
      'Premium': opp.total_estimated_premium || 0,
      'Expected Close Date': opp.expected_close_date || '',
      'Enterprise': opp.is_enterprise ? 'Yes' : 'No',
      'Timing': opp.timing || '', 'Authority': opp.authority || '', 'Budget': opp.budget || '', 'Contact': opp.contact || '',
      'Support Team': (opp.support_team || []).map(u => u.name).join(', '),
      'Notes': opp.notes || '',
      'Created': opp.created_at ? new Date(opp.created_at).toLocaleDateString() : '',
    }));
    const ws = XLSX.utils.json_to_sheet(data);
    ws['!cols'] = Object.keys(data[0] || {}).map(key => ({ wch: Math.max(key.length + 2, 15) }));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Policies');
    XLSX.writeFile(wb, `Policies_${new Date().toISOString().split('T')[0]}.xlsx`);
    toast.success(`Exported ${data.length} policies to Excel`);
  };

  const totalPipeline = filteredOpportunities.reduce((s, o) => s + (o.total_estimated_premium || 0), 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-pulse-slow text-[#007AFF] text-xl">Loading opportunities...</div>
      </div>
    );
  }

  const isQualifiedStage = ['Qualified Opportunity', 'Discovery', 'Quote', 'Negotiation', 'Closed Won', 'Closed Lost'].includes(formData.stage);

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="opportunities-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-extrabold text-[#0F172A] tracking-tight" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
            OPPORTUNITIES
          </h1>
          <p className="text-[#475569] mt-1">University → Sport → Athlete → Policy pipeline</p>
        </div>
        <div className="flex items-center gap-3">
          <Button variant="outline" className="border-[#E2E8F0] text-[#475569] font-bold uppercase tracking-wide"
            onClick={exportToExcel} disabled={filteredOpportunities.length === 0} data-testid="export-opportunities-btn">
            <Download className="w-4 h-4 mr-2" /> Export Excel
          </Button>
          <Dialog open={isCreateOpen} onOpenChange={(open) => { if (!open) resetForm(); setIsCreateOpen(open); }}>
            <DialogTrigger asChild>
              <Button className="bg-[#007AFF] hover:bg-[#0066CC] text-white font-bold uppercase tracking-wide shadow-lg hover:shadow-xl transition-all" data-testid="create-opportunity-btn">
                <Plus className="w-4 h-4 mr-2" /> New Policy
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-white border-[#E2E8F0] text-[#0F172A] max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="text-xl font-bold" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
                  {editOpp ? 'EDIT POLICY' : 'NEW POLICY'}
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-[#475569] text-sm uppercase tracking-wider font-medium">University</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button variant="outline" role="combobox" className="w-full justify-between bg-[#F8FAFC] border-[#E2E8F0] text-[#0F172A] hover:bg-[#F1F5F9]" data-testid="opp-account-select">
                          {formData.account_id ? accounts.find(a => a.id === formData.account_id)?.university_name : "Search universities..."}
                          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-[300px] p-0 bg-white border-[#E2E8F0]">
                        <Command>
                          <CommandInput placeholder="Search university..." className="h-9" />
                          <CommandList>
                            <CommandEmpty>No university found.</CommandEmpty>
                            <CommandGroup>
                              {accounts.map((account) => (
                                <CommandItem key={account.id} value={account.university_name}
                                  onSelect={() => setFormData({ ...formData, account_id: account.id })} className="cursor-pointer">
                                  <Check className={`mr-2 h-4 w-4 ${formData.account_id === account.id ? "opacity-100" : "opacity-0"}`} />
                                  {account.university_name}
                                </CommandItem>
                              ))}
                            </CommandGroup>
                          </CommandList>
                        </Command>
                      </PopoverContent>
                    </Popover>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-[#475569] text-sm uppercase tracking-wider font-medium">Sport</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button variant="outline" role="combobox" className="w-full justify-between bg-[#F8FAFC] border-[#E2E8F0] text-[#0F172A] hover:bg-[#F1F5F9]" data-testid="opp-sport-select">
                          {formData.sport || "Search sports..."}
                          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-[250px] p-0 bg-white border-[#E2E8F0]">
                        <Command>
                          <CommandInput placeholder="Search sport..." className="h-9" />
                          <CommandList>
                            <CommandEmpty>No sport found.</CommandEmpty>
                            <CommandGroup>
                              {SPORTS.map((sport) => (
                                <CommandItem key={sport} value={sport}
                                  onSelect={() => setFormData({ ...formData, sport })} className="cursor-pointer">
                                  <Check className={`mr-2 h-4 w-4 ${formData.sport === sport ? "opacity-100" : "opacity-0"}`} />
                                  {sport}
                                </CommandItem>
                              ))}
                            </CommandGroup>
                          </CommandList>
                        </Command>
                      </PopoverContent>
                    </Popover>
                  </div>
                </div>

                {/* Athlete + Policy + Premium */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-[#475569] text-sm uppercase tracking-wider font-medium">Athlete</Label>
                    <Input value={formData.athlete_name} onChange={(e) => setFormData({ ...formData, athlete_name: e.target.value })}
                      placeholder="Athlete name (or leave as TBD)" className="bg-[#F8FAFC] border-[#E2E8F0] text-[#0F172A]" data-testid="opp-athlete-input" />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-[#475569] text-sm uppercase tracking-wider font-medium">Policy Type</Label>
                    <Select value={formData.policy_type} onValueChange={(value) => setFormData({ ...formData, policy_type: value })}>
                      <SelectTrigger className="bg-[#F8FAFC] border-[#E2E8F0] text-[#0F172A]" data-testid="opp-policy-select">
                        <SelectValue placeholder="Select policy" />
                      </SelectTrigger>
                      <SelectContent className="bg-white border-[#E2E8F0]">
                        {POLICY_TYPES.map(p => (<SelectItem key={p} value={p} className="text-[#0F172A]" data-testid={`opp-policy-option-${p.replace(/\s+/g, '-').toLowerCase()}`}>{p}</SelectItem>))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-[#475569] text-sm uppercase tracking-wider font-medium">Estimated Premium</Label>
                    <div className="relative">
                      <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#94A3B8]" />
                      <Input type="number" value={formData.estimated_premium}
                        onChange={(e) => setFormData({ ...formData, estimated_premium: e.target.value })}
                        placeholder="Premium" className="pl-8 bg-[#F8FAFC] border-[#E2E8F0] text-[#0F172A]" data-testid="opp-premium-input" />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-[#475569] text-sm uppercase tracking-wider font-medium">Owner</Label>
                    <Select value={formData.owner_id} onValueChange={(value) => setFormData({ ...formData, owner_id: value })}>
                      <SelectTrigger className="bg-[#F8FAFC] border-[#E2E8F0] text-[#0F172A]" data-testid="opp-owner-select">
                        <SelectValue placeholder="Select owner" />
                      </SelectTrigger>
                      <SelectContent className="bg-white border-[#E2E8F0]">
                        {users.map(u => (<SelectItem key={u.id} value={u.id} className="text-[#0F172A]">{u.name}</SelectItem>))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-[#475569] text-sm uppercase tracking-wider font-medium">Support / Biz Dev</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button variant="outline" className="w-full justify-between bg-[#F8FAFC] border-[#E2E8F0] text-[#0F172A] hover:bg-[#F1F5F9]" data-testid="opp-support-select">
                          <div className="flex items-center gap-2">
                            <Users className="w-4 h-4 text-[#94A3B8]" />
                            {formData.support_ids.length > 0 ? `${formData.support_ids.length} selected` : "Add support team..."}
                          </div>
                          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-[250px] p-2 bg-white border-[#E2E8F0]">
                        <div className="space-y-2">
                          {users.filter(u => u.id !== formData.owner_id).map((u) => (
                            <div key={u.id} className="flex items-center gap-2 p-2 rounded hover:bg-[#F1F5F9] cursor-pointer"
                              onClick={() => {
                                const newIds = formData.support_ids.includes(u.id)
                                  ? formData.support_ids.filter(id => id !== u.id)
                                  : [...formData.support_ids, u.id];
                                setFormData({ ...formData, support_ids: newIds });
                              }}>
                              <Checkbox checked={formData.support_ids.includes(u.id)} className="border-[#E2E8F0]" />
                              <span className="text-sm text-[#0F172A]">{u.name}</span>
                            </div>
                          ))}
                        </div>
                      </PopoverContent>
                    </Popover>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-[#475569] text-sm uppercase tracking-wider font-medium">Stage</Label>
                    <Select value={formData.stage} onValueChange={(value) => setFormData({ ...formData, stage: value })}>
                      <SelectTrigger className="bg-[#F8FAFC] border-[#E2E8F0] text-[#0F172A]" data-testid="opp-stage-select">
                        <SelectValue placeholder="Select stage" />
                      </SelectTrigger>
                      <SelectContent className="bg-white border-[#E2E8F0]">
                        {STAGES.map(stage => (<SelectItem key={stage} value={stage} className="text-[#0F172A]" data-testid={`opp-stage-option-${stage.replace(/\s+/g, '-').toLowerCase()}`}>{stage}</SelectItem>))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-[#475569] text-sm uppercase tracking-wider font-medium">Expected Close Date</Label>
                    <Popover open={isDatePickerOpen} onOpenChange={setIsDatePickerOpen}>
                      <PopoverTrigger asChild>
                        <Button variant="outline" className="w-full justify-start text-left bg-[#F8FAFC] border-[#E2E8F0] text-[#0F172A] hover:bg-[#F1F5F9]" data-testid="opp-date-btn">
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {selectedDate ? format(selectedDate, 'PPP') : 'Pick a date'}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0 bg-white border-[#E2E8F0]">
                        <Calendar mode="single" selected={selectedDate}
                          onSelect={(date) => { setSelectedDate(date); setIsDatePickerOpen(false); }} initialFocus className="bg-white" />
                      </PopoverContent>
                    </Popover>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-[#475569] text-sm uppercase tracking-wider font-medium">Status</Label>
                    <Select value={formData.status} onValueChange={(value) => setFormData({ ...formData, status: value })}>
                      <SelectTrigger className="bg-[#F8FAFC] border-[#E2E8F0] text-[#0F172A]" data-testid="opp-status-select">
                        <SelectValue placeholder="Select status" />
                      </SelectTrigger>
                      <SelectContent className="bg-white border-[#E2E8F0]">
                        {STATUSES.map(status => (<SelectItem key={status} value={status} className="text-[#0F172A]">{status}</SelectItem>))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Qualification Fields */}
                <div className="space-y-3 pt-4 border-t border-[#E2E8F0]">
                  <div className="flex items-center gap-2">
                    <Label className="text-[#475569] text-sm uppercase tracking-wider font-medium">Qualification Info</Label>
                    {isQualifiedStage && (<Badge className="bg-[#FEF3C7] text-[#D97706] text-[10px] border-0">Required</Badge>)}
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="text-[#475569] text-xs">Timing</Label>
                      <Input value={formData.timing} onChange={(e) => setFormData({ ...formData, timing: e.target.value })}
                        placeholder="When do they want to buy?" className="bg-[#F8FAFC] border-[#E2E8F0] text-[#0F172A]" data-testid="opp-timing-input" />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-[#475569] text-xs">Authority</Label>
                      <Input value={formData.authority} onChange={(e) => setFormData({ ...formData, authority: e.target.value })}
                        placeholder="Decision maker" className="bg-[#F8FAFC] border-[#E2E8F0] text-[#0F172A]" data-testid="opp-authority-input" />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="text-[#475569] text-xs">Budget</Label>
                      <Input value={formData.budget} onChange={(e) => setFormData({ ...formData, budget: e.target.value })}
                        placeholder="Budget info" className="bg-[#F8FAFC] border-[#E2E8F0] text-[#0F172A]" data-testid="opp-budget-input" />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-[#475569] text-xs">Contact</Label>
                      <Input value={formData.contact} onChange={(e) => setFormData({ ...formData, contact: e.target.value })}
                        placeholder="Primary contact" className="bg-[#F8FAFC] border-[#E2E8F0] text-[#0F172A]" data-testid="opp-contact-input" />
                    </div>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className="text-[#475569] text-sm uppercase tracking-wider font-medium">Department</Label>
                  <Input value={formData.department} onChange={(e) => setFormData({ ...formData, department: e.target.value })}
                    placeholder="e.g., Athletics, Compliance" className="bg-[#F8FAFC] border-[#E2E8F0] text-[#0F172A]" data-testid="opp-department-input" />
                </div>
                <div className="space-y-2">
                  <Label className="text-[#475569] text-sm uppercase tracking-wider font-medium">Notes</Label>
                  <Textarea value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    placeholder="Additional notes..." className="bg-[#F8FAFC] border-[#E2E8F0] text-[#0F172A] min-h-[80px]" data-testid="opp-notes-input" />
                </div>

                <div className="flex gap-3 pt-4">
                  <Button type="button" variant="outline" onClick={resetForm} className="flex-1 border-[#E2E8F0] text-[#475569] hover:text-[#0F172A] hover:bg-[#F1F5F9]">Cancel</Button>
                  <Button type="submit" className="flex-1 bg-[#007AFF] hover:bg-[#0066CC] text-white font-bold" data-testid="opp-submit-btn">{editOpp ? 'Update' : 'Create'}</Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Filters */}
      <Card className="bg-white border-[#E2E8F0] shadow-sm">
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#94A3B8]" />
              <Input value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search university, sport, athlete, or policy..." className="pl-10 bg-[#F8FAFC] border-[#E2E8F0] text-[#0F172A]" data-testid="search-opportunities-input" />
            </div>
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="w-full md:w-40 bg-[#F8FAFC] border-[#E2E8F0] text-[#0F172A]" data-testid="filter-status-select"><SelectValue placeholder="Status" /></SelectTrigger>
              <SelectContent className="bg-white border-[#E2E8F0]">
                <SelectItem value="all" className="text-[#0F172A]">All Status</SelectItem>
                {STATUSES.map(s => (<SelectItem key={s} value={s} className="text-[#0F172A]">{s}</SelectItem>))}
              </SelectContent>
            </Select>
            <Select value={filterOwner} onValueChange={setFilterOwner}>
              <SelectTrigger className="w-full md:w-44 bg-[#F8FAFC] border-[#E2E8F0] text-[#0F172A]" data-testid="filter-owner-select"><SelectValue placeholder="Owner" /></SelectTrigger>
              <SelectContent className="bg-white border-[#E2E8F0]">
                <SelectItem value="all" className="text-[#0F172A]">All Owners</SelectItem>
                {users.map(u => (<SelectItem key={u.id} value={u.id} className="text-[#0F172A]">{u.name}</SelectItem>))}
              </SelectContent>
            </Select>
            <Select value={filterSport} onValueChange={setFilterSport}>
              <SelectTrigger className="w-full md:w-44 bg-[#F8FAFC] border-[#E2E8F0] text-[#0F172A]" data-testid="filter-sport-select"><SelectValue placeholder="Sport" /></SelectTrigger>
              <SelectContent className="bg-white border-[#E2E8F0]">
                <SelectItem value="all" className="text-[#0F172A]">All Sports</SelectItem>
                {SPORTS.map(s => (<SelectItem key={s} value={s} className="text-[#0F172A]">{s}</SelectItem>))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Hierarchical Roll-up View */}
      <Card className="bg-white border-[#E2E8F0] shadow-sm">
        <CardContent className="p-0">
          {grouped.length === 0 ? (
            <div className="text-center py-12 text-[#94A3B8]">No opportunities found</div>
          ) : (
            <div className="divide-y divide-[#E2E8F0]">
              {grouped.map((uni) => {
                const isOpen = expandedUnis.has(uni.id);
                return (
                  <div key={uni.id} data-testid={`uni-group-${uni.id}`}>
                    {/* University header */}
                    <button onClick={() => toggleUni(uni.id)}
                      className="w-full flex items-center justify-between px-5 py-4 hover:bg-[#F8FAFC] transition-colors text-left"
                      data-testid={`uni-toggle-${uni.id}`}>
                      <div className="flex items-center gap-3">
                        <ChevronRight className={`w-5 h-5 text-[#94A3B8] transition-transform ${isOpen ? 'rotate-90' : ''}`} />
                        <div className="w-10 h-10 rounded-lg bg-[#EFF6FF] flex items-center justify-center">
                          <Building2 className="w-5 h-5 text-[#007AFF]" />
                        </div>
                        <div>
                          <p className="font-bold text-[#0F172A] text-base">{uni.name}</p>
                          <p className="text-xs text-[#94A3B8]">{uni.sports.length} sport{uni.sports.length !== 1 ? 's' : ''} · {uni.count} polic{uni.count !== 1 ? 'ies' : 'y'}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-lg font-bold text-[#0F172A]" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>{formatCurrency(uni.total)}</p>
                        <p className="text-[10px] text-[#94A3B8] uppercase tracking-wider">Pipeline</p>
                      </div>
                    </button>

                    {/* Sports / Athletes / Policies */}
                    {isOpen && (
                      <div className="bg-[#FBFCFE] px-5 pb-4">
                        {uni.sports.map((sport) => (
                          <div key={sport.name} className="mt-3">
                            <div className="flex items-center justify-between px-3 py-2 bg-[#F1F5F9] rounded-md">
                              <span className="text-sm font-semibold text-[#334155] uppercase tracking-wide" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>{sport.name}</span>
                              <span className="text-sm font-bold text-[#475569]">{formatCurrency(sport.total)}</span>
                            </div>
                            {sport.athletes.map((ath) => (
                              <div key={ath.name} className="ml-3 mt-2">
                                <div className="flex items-center justify-between px-2 py-1.5">
                                  <div className="flex items-center gap-2">
                                    <UserIcon className="w-3.5 h-3.5 text-[#94A3B8]" />
                                    <span className={`text-sm font-medium ${ath.name === 'TBD' ? 'text-[#D97706]' : 'text-[#0F172A]'}`}>{ath.name}</span>
                                    {ath.name === 'TBD' && <Badge className="bg-[#FEF3C7] text-[#D97706] text-[9px] border-0">Set athlete</Badge>}
                                  </div>
                                  <span className="text-xs font-semibold text-[#64748B]">{formatCurrency(ath.total)}</span>
                                </div>
                                {/* Policy rows */}
                                <div className="ml-5 border-l-2 border-[#E2E8F0] pl-3 space-y-1.5">
                                  {ath.policies.map((opp) => (
                                    <div key={opp.id} className="flex items-center justify-between gap-2 py-2 px-3 rounded-lg bg-white border border-[#E2E8F0] hover:border-[#007AFF]/40 hover:shadow-sm transition-all cursor-pointer"
                                      onClick={() => setDetailOpp(opp)} data-testid={`policy-row-${opp.id}`}>
                                      <div className="flex items-center gap-2 flex-1 min-w-0">
                                        <Badge className={`${POLICY_COLORS[opp.policy_type] || 'bg-[#F1F5F9] text-[#475569]'} border-0 text-xs whitespace-nowrap`}>{opp.policy_type || 'Policy'}</Badge>
                                        <Badge variant="outline" className="border-[#E2E8F0] text-[#475569] bg-[#F8FAFC] text-xs whitespace-nowrap">{opp.stage}</Badge>
                                        <Badge className={`${getDaysColor(getDaysInStage(opp))} border-0 font-mono text-xs whitespace-nowrap`} data-testid={`days-in-stage-${opp.id}`}>
                                          <Clock className="w-3 h-3 mr-1" />{getDaysInStage(opp)}d
                                        </Badge>
                                        {opp.attachments_count > 0 && (
                                          <span className="flex items-center gap-1 text-xs text-[#94A3B8]" data-testid={`attachments-count-${opp.id}`}><Paperclip className="w-3 h-3" />{opp.attachments_count}</span>
                                        )}
                                      </div>
                                      <div className="flex items-center gap-3">
                                        <Avatar className="h-6 w-6 border border-[#E2E8F0]">
                                          <AvatarImage src={opp.owner?.avatar_url} />
                                          <AvatarFallback className="bg-[#F1F5F9] text-[#007AFF] text-[10px]">{getInitials(opp.owner?.name)}</AvatarFallback>
                                        </Avatar>
                                        <span className="text-sm font-bold text-[#0F172A] w-20 text-right" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>{formatCurrency(opp.total_estimated_premium || 0)}</span>
                                        <Badge className={`${getStatusColor(opp.status)} border-0 text-xs whitespace-nowrap`}>{opp.status}</Badge>
                                        <Button variant="ghost" size="icon" onClick={(e) => { e.stopPropagation(); openEditDialog(opp); }} className="h-7 w-7 text-[#475569] hover:text-[#007AFF] hover:bg-[#EFF6FF]" data-testid={`edit-opportunity-${opp.id}`}>
                                          <Edit className="w-3.5 h-3.5" />
                                        </Button>
                                        <Button variant="ghost" size="icon" onClick={(e) => { e.stopPropagation(); handleDelete(opp.id); }} className="h-7 w-7 text-[#475569] hover:text-[#FF3B30] hover:bg-[#FEF2F2]" data-testid={`delete-opportunity-${opp.id}`}>
                                          <Trash2 className="w-3.5 h-3.5" />
                                        </Button>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            ))}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Stats */}
      <div className="flex items-center justify-between text-sm text-[#94A3B8]">
        <span>{filteredOpportunities.length} of {opportunities.length} policies · {formatCurrency(totalPipeline)} pipeline</span>
        <div className="flex gap-4">
          <span>Open: {opportunities.filter(o => o.status === 'Open').length}</span>
          <span>Won: {opportunities.filter(o => o.status === 'Closed Won').length}</span>
          <span>Lost: {opportunities.filter(o => o.status === 'Closed Lost').length}</span>
        </div>
      </div>

      {/* Detail dialog (Details / Files / Notes) */}
      {detailOpp && (
        <OpportunityDetailDialog
          opp={detailOpp}
          open={!!detailOpp}
          onClose={() => setDetailOpp(null)}
          onEdit={(o) => { setDetailOpp(null); openEditDialog(o); }}
          onChanged={fetchData}
        />
      )}
    </div>
  );
};

export default OpportunitiesPage;
